import { useState, useEffect, useMemo } from 'react';
import { CardSummary, ATTRIBUTE_ICONS, ATTRIBUTE_NAMES, CARD_TYPE_NAMES } from '../../types/cards';
import { Card } from './Card';
import './CardDetailModal.css';

interface CardDetailModalProps {
  card: CardSummary | null;
  isOpen: boolean;
  onClose: () => void;
}

function gradeToLabel(grade: string): string {
  switch (grade) {
    case 'SS': return 'SS 등급';
    case 'S': return 'S 등급';
    case 'A': return 'A 등급';
    case 'B': return 'B 등급';
    case 'C': return 'C 등급';
    case 'D': return 'D 등급';
    default: return 'C 등급';
  }
}

function getGradeColor(grade: string): string {
  switch (grade) {
    case 'SS': return '#ffd700';
    case 'S': return '#ff8c69';
    case 'A': return '#87ceeb';
    case 'B': return '#90ee90';
    case 'C': return '#c0c0c0';
    case 'D': return '#a9a9a9';
    default: return '#c0c0c0';
  }
}

function getGradeRarity(grade: string): string {
  switch (grade) {
    case 'SS': return '울트라 레어';
    case 'S': return '레어';
    case 'A': return '언커먼';
    case 'B': return '커먼';
    case 'C': return '노말';
    case 'D': return '기본';
    default: return '노말';
  }
}

export function CardDetailModal({ card, isOpen, onClose }: CardDetailModalProps) {
  const [isFlipped, setIsFlipped] = useState(false);
  const [hasAnimation, setHasAnimation] = useState(true);

  const isMobile = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(pointer: coarse)').matches;
  }, []);

  useEffect(() => {
    if (isOpen && card) {
      setIsFlipped(false);
      setHasAnimation(!isMobile);
    }
  }, [isOpen, card, isMobile]);

  useEffect(() => {
    if (isOpen) {
      document.documentElement.classList.add('modal-open');
      document.body.classList.add('modal-open');
    } else {
      document.documentElement.classList.remove('modal-open');
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.documentElement.classList.remove('modal-open');
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  if (!isOpen || !card) return null;

  const gradeColor = getGradeColor(card.grade);
  const rarity = getGradeRarity(card.grade);

  return (
    <div className="card-modal-overlay" onClick={onClose}>
      <div className="card-modal" onClick={(e) => e.stopPropagation()}>
        <button className="card-modal__close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        
        <div className={`card-modal__content card-modal__content--${card.grade.toLowerCase()}`}>
          {/* 카드 이미지 섹션 - 플립 가능 */}
          <div className="card-modal__image-container">
            <Card
              card={card}
              showBack={true}
              isFlipped={isFlipped}
              flipOnClick={true}
              onFlip={() => setIsFlipped(!isFlipped)}
            />
          </div>

          {/* 파티클 효과 */}
          {hasAnimation && !isMobile && (
            <div className="card-modal__particles">
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="card-modal__particle"
                  style={{
                    '--delay': `${i * 0.1}s`,
                    '--x': `${Math.random() * 100}%`,
                    '--y': `${Math.random() * 100}%`,
                  } as React.CSSProperties}
                />
              ))}
            </div>
          )}
          
          {/* 정보 섹션 */}
          <div className="card-modal__info-section">
            <div className="card-modal__header-info">
              <div>
                <h2 className="card-modal__name">{card.name}</h2>
                <div className="card-modal__rarity">{rarity}</div>
              </div>
              <div className="card-modal__id">#{card.id.toString().padStart(4, '0')}</div>
            </div>

            <div className="card-modal__stats-grid">
              <div className="card-modal__stat-card">
                <div className="card-modal__stat-label">등급</div>
                <div 
                  className="card-modal__stat-value"
                  style={{ '--grade-color': gradeColor } as React.CSSProperties}
                >
                  {card.grade}
                </div>
              </div>
              <div className="card-modal__stat-card">
                <div className="card-modal__stat-label">보유 개수</div>
                <div className="card-modal__stat-value card-modal__stat-value--count">
                  {card.count}
                </div>
              </div>
              {card.card_type && (
                <div className="card-modal__stat-card">
                  <div className="card-modal__stat-label">카드 타입</div>
                  <div className="card-modal__stat-value">
                    {CARD_TYPE_NAMES[card.card_type]}
                  </div>
                </div>
              )}
              {card.attribute && (
                <div className="card-modal__stat-card">
                  <div className="card-modal__stat-label">속성</div>
                  <div className="card-modal__stat-value card-modal__stat-value--attribute">
                    <span className="card-modal__attribute-icon">{ATTRIBUTE_ICONS[card.attribute]}</span>
                    <span>{ATTRIBUTE_NAMES[card.attribute]}</span>
                  </div>
                </div>
              )}
            </div>

            {/* 전투 속성 */}
            {(card.cost !== undefined || card.attack !== undefined || card.defense !== undefined || card.hp !== undefined) && (
              <div className="card-modal__battle-stats">
                <div className="card-modal__battle-stats-label">전투 속성</div>
                <div className="card-modal__battle-stats-grid">
                  {card.cost !== undefined && (
                    <div className="card-modal__battle-stat">
                      <div className="card-modal__battle-stat-icon">⚡</div>
                      <div className="card-modal__battle-stat-info">
                        <div className="card-modal__battle-stat-label">코스트</div>
                        <div className="card-modal__battle-stat-value">{card.cost}</div>
                      </div>
                    </div>
                  )}
                  {card.attack !== undefined && (
                    <div className="card-modal__battle-stat">
                      <div className="card-modal__battle-stat-icon">⚔️</div>
                      <div className="card-modal__battle-stat-info">
                        <div className="card-modal__battle-stat-label">공격력</div>
                        <div className="card-modal__battle-stat-value">{card.attack}</div>
                      </div>
                    </div>
                  )}
                  {card.defense !== undefined && (
                    <div className="card-modal__battle-stat">
                      <div className="card-modal__battle-stat-icon">🛡️</div>
                      <div className="card-modal__battle-stat-info">
                        <div className="card-modal__battle-stat-label">방어력</div>
                        <div className="card-modal__battle-stat-value">{card.defense}</div>
                      </div>
                    </div>
                  )}
                  {card.hp !== undefined && (
                    <div className="card-modal__battle-stat">
                      <div className="card-modal__battle-stat-icon">❤️</div>
                      <div className="card-modal__battle-stat-info">
                        <div className="card-modal__battle-stat-label">체력</div>
                        <div className="card-modal__battle-stat-value">{card.hp}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 스킬 정보 */}
            {card.skill_name && (
              <div className="card-modal__skill-section">
                <div className="card-modal__skill-header">
                  <div className="card-modal__skill-icon">✨</div>
                  <div className="card-modal__skill-name">{card.skill_name}</div>
                </div>
                {card.skill_description && (
                  <p className="card-modal__skill-description">{card.skill_description}</p>
                )}
              </div>
            )}

            <div className="card-modal__description-section">
              <div className="card-modal__description-label">카드 설명</div>
              <p className="card-modal__description">{card.description}</p>
            </div>

            {/* 등급별 특별 정보 */}
            {card.grade === 'SS' && (
              <div className="card-modal__special-info">
                <div className="card-modal__special-icon">⭐</div>
                <div className="card-modal__special-text">
                  이 카드는 최고 등급의 희귀 카드입니다!
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
